import { MailCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { AuthError } from "@/auth/types";
import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/button";

import { AuthField, AuthLayout, FormAlert, useAuthErrorText } from "./components";

/**
 * `/forgot` — always the same answer whether or not the e-mail is a staff account (no account
 * discovery); only a network / rate-limit problem is shown.
 */
export function ForgotPage() {
  const { t } = useTranslation("auth");
  const { api } = useAuth();
  const errorText = useAuthErrorText();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending || !email.trim()) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err: unknown) {
      if (err instanceof AuthError && (err.key === "network" || err.key === "rate_limited")) {
        setError(errorText(err));
      } else {
        setSent(true);
      }
    } finally {
      setPending(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title={t("forgot.sent_title")}>
        <div role="status" className="flex flex-col gap-3">
          <MailCheck className="size-touch" aria-hidden />
          <p>{t("forgot.sent_body")}</p>
          <Link to="/login" className="min-h-touch self-start py-2 underline underline-offset-4">
            {t("forgot.back")}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("forgot.title")} intro={t("forgot.intro")}>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        <AuthField
          label={t("forgot.email")}
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        <FormAlert message={error} />
        <Button type="submit" size="lg" disabled={pending}>
          {t("forgot.submit")}
        </Button>
        <Link
          to="/login"
          className="min-h-touch self-start py-2 text-sm underline underline-offset-4"
        >
          {t("forgot.back")}
        </Link>
      </form>
    </AuthLayout>
  );
}
